import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, CardView, rem, Separator, SettingsItemNavigate, } from 'design-system';
import { IconBank, IconBoldArrow, IconBoldCalendar, IconSmartRule, } from 'design-system/icons';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import { IconLetters } from 'design-system/icons/IconLetters';
import { smartRuleTypeLabels } from 'utils/types/navigation';
import { useAppSelector } from 'store/hooks';
import { selectActiveConnections, selectDefaultCurrency, } from 'reducers/selectors';
import getListAccounts from 'utils/api/getListAccounts';
import getListOfflineAccounts from 'utils/api/getListOfflineAccounts';
import { formatAmount } from 'utils';
import Status from 'design-system/Status';
import Squircle from 'design-system/Squircle';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { SmartRuleIconForTransactionType } from './SmartRuleIconForTransactionType';
import { getPeriodText } from '../utils';
const SmartRuleDetailsCard = ({ item, onPress, isCompact }) => {
    const { accountIds, counterpartRegex, type, maxAmount, minAmount, minDayOfMonth, maxDayOfMonth, } = item.filter.criteria;
    const colors = useColors();
    const currency = useAppSelector(selectDefaultCurrency);
    const activeConnections = useAppSelector(selectActiveConnections);
    const navigation = useMainStackNavigation();
    const targetAccount = useMemo(() => getListOfflineAccounts(activeConnections).find((acc) => acc.id === item.targetAccountId), [activeConnections, item.targetAccountId]);
    const period = useMemo(() => getPeriodText(minDayOfMonth ?? 1, maxDayOfMonth ?? 31), [maxDayOfMonth, minDayOfMonth]);
    const fromText = useMemo(() => {
        if (accountIds.length === 1) {
            const foundAccount = getListAccounts(activeConnections).find((acc) => accountIds[0] === acc.id);
            if (foundAccount) {
                return foundAccount.name;
            }
        }
        return `${accountIds.length} accounts selected`;
    }, [accountIds, activeConnections]);
    const openCreatedTransactions = useCallback(() => {
        navigation.navigateDeprecated('SmartRuleCreatedTransactions', {
            id: item.id,
            createdAt: item.createdAt,
        });
    }, [item.createdAt, item.id]);
    if (!targetAccount)
        return null;
    return (<CardView noMarginTop>
      <View style={styles.header}>
        <View>
          <Text Text-16>{item.ruleName}</Text>
          <Status active={item.isActive} activeText="Working" inactiveText="Inactive" inActiveColor={colors.status.offline}/>
        </View>
        <Squircle color={colors.background.dark}>
          <SmartRuleIconForTransactionType type={type}/>
        </Squircle>
      </View>
      <Separator />
      <SettingsItemNavigate disabled icon={<IconBank />} title={fromText} description="From"/>
      <SettingsItemNavigate disabled icon={<IconLetters gradientKey="darkPurple"/>} title={counterpartRegex} description="Reference"/>
      {!isCompact && (<>
          <SettingsItemNavigate disabled icon={<IconBoldArrow direction="left"/>} title={minAmount === undefined
                ? 'Not set'
                : formatAmount(Math.abs(minAmount), currency)} description="Minimum amount"/>
          <SettingsItemNavigate disabled icon={<IconBoldArrow direction="right" gradientKey="skyBlue"/>} title={maxAmount === undefined
                ? 'Not set'
                : formatAmount(Math.abs(maxAmount), currency)} description="Maximum amount"/>
          <SettingsItemNavigate disabled icon={<SmartRuleIconForTransactionType type={type}/>} title={smartRuleTypeLabels[type]} description="Transaction type"/>
          <SettingsItemNavigate disabled icon={<IconBoldCalendar />} title={period} description="Period"/>
        </>)}
      <SettingsItemNavigate disabled icon={<IconBank />} title={targetAccount.name} description="To"/>
      <Separator />
      <SettingsItemNavigate disabled={isCompact || !item.createdTransactionsCount} icon={<IconSmartRule gradientKey="peach"/>} title={item.createdTransactionsCount || '0'} description="Created transactions" onPress={openCreatedTransactions}/>
      {isCompact ? (<Button brandReversed small square title="See details" onPress={onPress} style={styles.button}/>) : null}
    </CardView>);
};
const styles = StyleSheet.create({
    header: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    button: {
        margin: rem(16),
        marginTop: rem(8),
    },
});
export default SmartRuleDetailsCard;
